import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useParams } from "react-router";
import { api } from "@/api/client";

type ProjectContextValue = ReturnType<typeof useProjectContextValue>;

function useProjectContextValue() {
  const { slug = "" } = useParams();
  const query = api.useQuery("get", "/api/projects/{slug}", { params: { path: { slug } } });
  const role = query.data?.role;
  const project = query.data?.project;
  const admin = role === "project_admin" || role === "platform_admin";
  // The query result object is new on every render; re-publish it only when its observable state moves.
  const { error, status, fetchStatus, refetch } = query;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ slug, project, role, admin, query }), [slug, project, role, admin, error, status, fetchStatus, refetch]);
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

/** App-owned current-project query shared by the project route and its feature pages. */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const project = useProjectContextValue();
  return <ProjectContext.Provider value={project}>{children}</ProjectContext.Provider>;
}

/** Current project identity and permission projection for pages inside `p/:slug`. */
export function useProjectContext() {
  const project = useContext(ProjectContext);
  if (!project) throw new Error("ProjectProvider is missing for this route.");
  return project;
}
